import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Clock, Calendar, AlertCircle, Check } from 'lucide-react';
import { format, addDays, subDays, isValid, parseISO } from 'date-fns';
import { createPayrollRequest, checkExistingPayrollEntry } from '../lib/payrollRequests';
import toast from 'react-hot-toast';

interface AddMissingEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  resourceId: string;
  businessId: string;
  onSuccess: () => void;
}

const AddMissingEntryModal: React.FC<AddMissingEntryModalProps> = ({
  isOpen,
  onClose,
  resourceId,
  businessId,
  onSuccess
}) => {
  const [workDate, setWorkDate] = useState('');
  const [clockIn, setClockIn] = useState('');
  const [clockOut, setClockOut] = useState('');
  const [breakStart, setBreakStart] = useState('');
  const [breakEnd, setBreakEnd] = useState('');
  const [notes, setNotes] = useState('');
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [existingEntryCheck, setExistingEntryCheck] = useState<{loading: boolean, exists: boolean}>({
    loading: false,
    exists: false
  });

  // Validation states
  const [errors, setErrors] = useState<{[key: string]: string}>({});

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setWorkDate('');
      setClockIn('');
      setClockOut('');
      setBreakStart('');
      setBreakEnd('');
      setNotes('');
      setReason('');
      setErrors({});
      setExistingEntryCheck({ loading: false, exists: false });
    }
  }, [isOpen]);

  // Check for existing entry when work date changes
  useEffect(() => {
    const checkExisting = async () => {
      if (!workDate || !resourceId || !businessId) return;
      
      setExistingEntryCheck({ loading: true, exists: false });
      
      try {
        const exists = await checkExistingPayrollEntry(resourceId, businessId, workDate);
        setExistingEntryCheck({ loading: false, exists });
        
        if (exists) {
          setErrors(prev => ({ ...prev, workDate: 'An entry already exists for this date' }));
        } else {
          setErrors(prev => {
            const { workDate, ...rest } = prev;
            return rest;
          });
        }
      } catch (error) {
        console.error('Error checking existing entry:', error);
        setExistingEntryCheck({ loading: false, exists: false });
      }
    };

    checkExisting();
  }, [workDate, resourceId, businessId]);

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};

    if (!workDate) {
      newErrors.workDate = 'Work date is required';
    } else {
      const today = new Date();
      const selectedDate = new Date(workDate);
      if (selectedDate > today) {
        newErrors.workDate = 'Cannot add future entries';
      }
    }

    if (!clockIn) {
      newErrors.clockIn = 'Clock in time is required';
    }

    if (!clockOut) {
      newErrors.clockOut = 'Clock out time is required';
    }

    if (clockIn && clockOut) {
      const clockInTime = new Date(`${workDate}T${clockIn}`);
      const clockOutTime = new Date(`${workDate}T${clockOut}`);
      
      if (clockInTime >= clockOutTime) {
        newErrors.clockOut = 'Clock out must be after clock in';
      }
    }

    if (breakStart && breakEnd) {
      const breakStartTime = new Date(`${workDate}T${breakStart}`);
      const breakEndTime = new Date(`${workDate}T${breakEnd}`);
      
      if (breakStartTime >= breakEndTime) {
        newErrors.breakEnd = 'Break end must be after break start';
      }
      
      if (clockIn && clockOut) {
        const clockInTime = new Date(`${workDate}T${clockIn}`);
        const clockOutTime = new Date(`${workDate}T${clockOut}`);
        
        if (breakStartTime < clockInTime || breakEndTime > clockOutTime) {
          newErrors.breakStart = 'Break must be within work hours';
        }
      }
    }

    if (!reason.trim()) {
      newErrors.reason = 'Please provide a reason for this request';
    }

    if (existingEntryCheck.exists) {
      newErrors.workDate = 'An entry already exists for this date';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const calculateTotals = () => {
    if (!clockIn || !clockOut) return { minutes: 0, hours: 0, earnings: 0 };

    let totalMs = new Date(`${workDate}T${clockOut}`).getTime() - new Date(`${workDate}T${clockIn}`).getTime();
    
    // Subtract break time
    if (breakStart && breakEnd) {
      const breakMs = new Date(`${workDate}T${breakEnd}`).getTime() - new Date(`${workDate}T${breakStart}`).getTime();
      totalMs -= breakMs;
    }
    
    const totalMinutes = Math.max(0, Math.floor(totalMs / (1000 * 60)));
    const hours = totalMinutes / 60;
    
    return {
      minutes: totalMinutes,
      hours: Math.round(hours * 100) / 100,
      earnings: 0 // Will be calculated server-side based on hourly rate
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const requestData = {
        business_id: businessId,
        resource_id: resourceId,
        request_type: 'add' as const,
        requested_work_date: workDate,
        requested_clock_in: `${workDate}T${clockIn}:00.000Z`,
        requested_clock_out: `${workDate}T${clockOut}:00.000Z`,
        requested_break_start: breakStart ? `${workDate}T${breakStart}:00.000Z` : undefined,
        requested_break_end: breakEnd ? `${workDate}T${breakEnd}:00.000Z` : undefined,
        requested_notes: notes || undefined,
        request_reason: reason,
      };

      await createPayrollRequest(requestData);
      
      toast.success('Missing entry request submitted successfully!');
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error submitting missing entry request:', error);
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const totals = calculateTotals();

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
              <Clock className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Add Missing Entry</h2>
              <p className="text-sm text-gray-500">Request to add a missing timeclock entry</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="h-8 w-8 rounded-lg hover:bg-gray-100 flex items-center justify-center transition-colors"
          >
            <X className="h-4 w-4 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Work Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Work Date *
            </label>
            <input
              type="date"
              value={workDate}
              onChange={(e) => setWorkDate(e.target.value)}
              max={format(new Date(), 'yyyy-MM-dd')}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.workDate ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {existingEntryCheck.loading && (
              <p className="mt-1 text-sm text-gray-500">Checking for existing entries...</p>
            )}
            {existingEntryCheck.exists && (
              <div className="mt-1 flex items-center gap-1 text-sm text-red-600">
                <AlertCircle className="h-4 w-4" />
                An entry already exists for this date
              </div>
            )}
            {errors.workDate && (
              <p className="mt-1 text-sm text-red-600">{errors.workDate}</p>
            )}
          </div>

          {/* Time Inputs */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Clock In *
              </label>
              <input
                type="time"
                value={clockIn}
                onChange={(e) => setClockIn(e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  errors.clockIn ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {errors.clockIn && (
                <p className="mt-1 text-sm text-red-600">{errors.clockIn}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Clock Out *
              </label>
              <input
                type="time"
                value={clockOut}
                onChange={(e) => setClockOut(e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  errors.clockOut ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {errors.clockOut && (
                <p className="mt-1 text-sm text-red-600">{errors.clockOut}</p>
              )}
            </div>
          </div>

          {/* Break Time Inputs */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Break Start (Optional)
              </label>
              <input
                type="time"
                value={breakStart}
                onChange={(e) => setBreakStart(e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  errors.breakStart ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {errors.breakStart && (
                <p className="mt-1 text-sm text-red-600">{errors.breakStart}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Break End (Optional)
              </label>
              <input
                type="time"
                value={breakEnd}
                onChange={(e) => setBreakEnd(e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  errors.breakEnd ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {errors.breakEnd && (
                <p className="mt-1 text-sm text-red-600">{errors.breakEnd}</p>
              )}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes (Optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Any additional notes about this entry..."
            />
          </div>

          {/* Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason for Request *
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.reason ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Please explain why this entry is missing (e.g., forgot to clock in, system issue, etc.)"
            />
            {errors.reason && (
              <p className="mt-1 text-sm text-red-600">{errors.reason}</p>
            )}
          </div>

          {/* Summary */}
          {totals.minutes > 0 && (
            <div className="bg-blue-50 rounded-xl p-4">
              <h3 className="text-sm font-medium text-blue-900 mb-2">Entry Summary</h3>
              <div className="space-y-1 text-sm text-blue-700">
                <p><strong>Total Time:</strong> {Math.floor(totals.hours)}h {totals.minutes % 60}m</p>
                <p><strong>Total Minutes:</strong> {totals.minutes} minutes</p>
                <p className="text-xs text-blue-600">Earnings will be calculated based on your hourly rate</p>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || existingEntryCheck.exists || existingEntryCheck.loading}
              className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  Submitting...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4" />
                  Submit Request
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default AddMissingEntryModal;